import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Container, Paper, Chip, MenuItem,
  TextField, Skeleton,
} from '@mui/material';
import { LocationOn, Event as EventIcon, Star, Redeem } from '@mui/icons-material';
import httpClient from '../../utils/httpClient';
import eventsService from '../../services/eventsService';
import { colors } from '../../theme/theme';
import { useTranslation } from 'react-i18next';

interface VenueCard {
  id: string;
  name: string;
  slug: string;
  cover_photo_url: string | null;
  zone_name: string;
  tier: string;
  avg_rating: number | null;
  active_event_count: number;
  address: string;
  courtesy_discount: string | null;
  distance_km?: number;
  is_open_now: boolean;
  closes_at: string | null;
  opens_at: string | null;
}

export default function VenuesBrowsePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [venues, setVenues] = useState<VenueCard[]>([]);
  const [loading, setLoading] = useState(true);
  const [zones, setZones] = useState<{ id: string; name: string }[]>([]);
  const [zoneFilter, setZoneFilter] = useState('');

  useEffect(() => {
    eventsService.getZones().then(setZones).catch(() => { /* no-op */ });
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = zoneFilter ? `?zone_id=${encodeURIComponent(zoneFilter)}` : '';
    httpClient.get(`/venues/public${params}`)
      .then(r => setVenues(r.data))
      .catch(() => { /* no-op */ })
      .finally(() => setLoading(false));
  }, [zoneFilter]);

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 800, color: '#FFFFFF' }}>
            ☕ {t('venues.coffeeshops')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('venues.explore')}
          </Typography>
        </Box>
        <TextField select size="small" value={zoneFilter} onChange={e => setZoneFilter(e.target.value)}
          label="Filter by zone" sx={{ minWidth: 180 }}>
          <MenuItem value="">All Zones</MenuItem>
          {zones.map(z => <MenuItem key={z.id} value={z.id}>{z.name}</MenuItem>)}
        </TextField>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
          {[1, 2, 3].map(i => (
            <Box key={i} sx={{ flex: '1 1 300px', maxWidth: { xs: '100%', sm: '48%', md: '31%' } }}>
              <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 3 }} />
              <Skeleton height={30} width="70%" sx={{ mt: 1 }} />
              <Skeleton height={20} width="50%" />
            </Box>
          ))}
        </Box>
      ) : venues.length === 0 ? (
        <Paper sx={{ p: 6, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary">No venues in this zone yet</Typography>
        </Paper>
      ) : (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
          {venues.map(v => (
            <Box key={v.id} sx={{ flex: '1 1 300px', maxWidth: { xs: '100%', sm: '48%', md: '31%' } }}>
              <Paper
                sx={{
                  borderRadius: 3, overflow: 'hidden', cursor: 'pointer',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  '&:hover': { transform: 'translateY(-4px)', boxShadow: 4 },
                }}
                onClick={() => navigate(`/venues/${v.slug}`)}
              >
                {/* Cover Photo */}
                <Box sx={{
                  height: 180,
                  backgroundImage: `url(${v.cover_photo_url || 'https://images.unsplash.com/photo-1554118811-1e0d58224f24?w=600'})`,
                  backgroundSize: 'cover', backgroundPosition: 'center',
                  position: 'relative',
                }}>
                  <Chip label={v.tier} size="small" sx={{
                    position: 'absolute', top: 12, right: 12,
                    bgcolor: v.tier === 'premium' ? 'rgba(242,153,74,0.9)' : 'rgba(255,255,255,0.9)',
                    color: v.tier === 'premium' ? 'white' : colors.neutral[700],
                    fontWeight: 600, textTransform: 'capitalize',
                  }} />
                  {v.active_event_count > 0 && (
                    <Chip icon={<EventIcon sx={{ fontSize: 14 }} />}
                      label={`${v.active_event_count} event${v.active_event_count > 1 ? 's' : ''}`}
                      size="small" sx={{
                        position: 'absolute', bottom: 12, left: 12,
                        bgcolor: 'rgba(255,255,255,0.9)', fontWeight: 600,
                      }} />
                  )}
                </Box>

                {/* Info */}
                <Box sx={{ p: 2 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{v.name}</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                      <LocationOn sx={{ fontSize: 14 }} /> {v.zone_name}
                      {v.distance_km !== undefined && ` · ${v.distance_km} km`}
                    </Typography>
                    <Chip
                      label={v.is_open_now ? 'Open' : 'Closed'}
                      size="small"
                      sx={{
                        height: 20,
                        fontSize: '0.65rem',
                        fontWeight: 600,
                        bgcolor: v.is_open_now ? 'rgba(76,175,80,0.12)' : 'rgba(158,158,158,0.12)',
                        color: v.is_open_now ? '#2E7D32' : '#757575',
                      }}
                    />
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 1 }}>
                    {v.avg_rating ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Star sx={{ fontSize: 16, color: colors.warning.main }} />
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>{v.avg_rating}</Typography>
                      </Box>
                    ) : (
                      <Typography variant="caption" color="text.secondary">{t('venues.new_venue')}</Typography>
                    )}
                    {v.courtesy_discount && (
                      <Chip
                        icon={<Redeem sx={{ fontSize: 11 }} />}
                        label={v.courtesy_discount}
                        size="small"
                        sx={{ bgcolor: colors.success.subtle, color: colors.success.dark, fontSize: '0.65rem' }}
                      />
                    )}
                  </Box>
                </Box>
              </Paper>
            </Box>
          ))}
        </Box>
      )}
    </Container>
  );
}
